// src/scenes/ProfileEmailManager.js
// Extracts email verification and company email functionality from ProfileScene
import { getCompanyEmailStatus } from "../services/firmAuthService.js";

export default class ProfileEmailManager {
  constructor(scene) {
    this.scene = scene;
  }

  // Load company email status from Firebase
  async loadCompanyEmailStatus(userId) {
    try {
      const companyEmailStatus = await getCompanyEmailStatus(userId);
      return companyEmailStatus;
    } catch (error) {
      console.warn("Could not load company email status:", error);
      return { companyEmail: null, verified: false, domain: null };
    }
  }

  // Get company email status display text
  getCompanyEmailStatus() {
    const status = this.scene.companyEmailStatus || this.scene.profileData;
    const companyEmail = status?.companyEmail || this.scene.profileData?.companyEmail;
    const isVerified = status?.verified || this.scene.profileData?.companyEmailVerified;
    
    if (!companyEmail) return "Not Associated";
    if (!isVerified) return `${companyEmail} (Pending)`;
    return `✓ ${companyEmail}`;
  }

  // Get company email status color for UI elements
  getCompanyEmailColor() {
    const status = this.scene.companyEmailStatus || this.scene.profileData;
    const companyEmail = status?.companyEmail || this.scene.profileData?.companyEmail;
    const isVerified = status?.verified || this.scene.profileData?.companyEmailVerified;
    
    if (!companyEmail) return "#AAAAAA";  // Gray for not associated
    if (!isVerified) return "#FFAA00";    // Amber for pending
    return "#00FF00";                     // Green for verified
  }

  // Get company email status color for Matrix theme UI elements
  getCompanyEmailStatusColor() {
    const status = this.scene.companyEmailStatus || this.scene.profileData;
    const companyEmail = status?.companyEmail || this.scene.profileData?.companyEmail;
    const isVerified = status?.verified || this.scene.profileData?.companyEmailVerified;
    
    if (!companyEmail) return '#666666';  // Matrix gray
    if (!isVerified) return '#FFAA00';    // Matrix amber
    return '#00FF00';                     // Achievement green
  }

  // Check if user has firm access
  hasCompanyEmailAccess() {
    return this.scene.profileData?.companyEmail && this.scene.profileData?.companyEmailVerified;
  }

  // Get company/firm name from email
  getCompanyName() {
    if (!this.hasCompanyEmailAccess()) return null;
    
    const companyEmail = this.scene.profileData?.companyEmail;
    if (!companyEmail) return null;
    
    // Extract company name from email domain
    const domain = companyEmail.split('@')[1];
    if (!domain) return null;
    
    return domain.split('.')[0]?.toUpperCase() || 'FIRM';
  }

  // Get firm access information for UI display
  getFirmAccessInfo() {
    const hasAccess = this.hasCompanyEmailAccess();
    const companyName = this.getCompanyName();
    
    return {
      hasAccess,
      companyName: companyName || 'FIRM',
      email: this.scene.profileData?.companyEmail || null,
      verified: this.scene.profileData?.companyEmailVerified || false
    };
  }

  // Create formatted text for company email status in UI
  getFormattedCompanyEmailText() {
    const status = this.getCompanyEmailStatus();
    const color = this.getCompanyEmailColor();
    
    return {
      text: status,
      color: color,
      verified: this.hasCompanyEmailAccess()
    };
  }

  // Create BBCode formatted text for company email status
  getBBCodeCompanyEmailText() {
    const formatted = this.getFormattedCompanyEmailText();
    return `[color=${formatted.color}]${formatted.text}[/color]`;
  }
}
